<template>
  <div
    :class="[
      $style.infoPage,
      {
        'page-vip': pageId === 'vip',
        pageProvider: pageId === 'providers',
        'slot-themes': pageId === 'slot-themes',
        actions: pageId === 'actions',
        infoPageCustomFaqStyle: isWithCustomFaqStyles,
      },
    ]"
  >
    <div
      :class="[
        $style.infoBlocks,
        $style.infoPageSpaceTop,
        { [$style.infoPageNoSpaceTop]: isNoAdditionalSpaceTop },
      ]"
    >
      <component
        :is="block.cmp"
        v-for="(block, index) of pageBlocks"
        :key="index"
        :data="block"
        :class="`${$style.infoBlocksPartials} ${block.class}`"
      />
      <BitButton
        v-if="isPageWithPwaBlock"
        type="secondary"
        is-fluid-width
        :class="$style.infoPageInstallAppBtn"
        @click="storeModals.openPwaInstallModal"
      >
        {{ storeButtonsTitle.installApp }}
      </BitButton>
      <Breadcrumbs v-if="breadcrumbsBlock.length" :breadcrumbs-list="breadcrumbsBlock" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitButton } from '@bit/ui-kit';
import { computed, onMounted } from 'vue';
// import InfoPageActionsBannerBlock from '~/components/infoPage/actionsBannerBlock.vue';
import InfoPageActionsBlock from '~/components/infoPage/actionsBlock.vue';
import InfoPageAlphabetBlock from '~/components/infoPage/alphabetBlock.vue';
import InfoPageBonusPromoBlock from '~/components/infoPage/bonusPromoBlock.vue';
import InfoPageCategoriesBlock from '~/components/infoPage/categoriesBlock.vue';
import InfoPageCollections from '~/components/infoPage/collections.vue';
import InfoPageContactBlock from '~/components/infoPage/contactBlock.vue';
import InfoPageFaqBlock from '~/components/infoPage/faqBlock.vue';
import InfoPageFortuneWheelBlock from '~/components/infoPage/fortuneWheelBlock.vue';
import InfoPageDownloadFileBlock from '~/components/infoPage/infoPageDownloadFileBlock.vue';
import InfoPageNotificationBlock from '~/components/infoPage/notificationBlock.vue';
import InfoPagePaymentBlock from '~/components/infoPage/paymentBlock.vue';
import InfoPageProvidersBlock from '~/components/infoPage/providersBlock.vue';
import InfoPagePwaBlock from '~/components/infoPage/pwaBlock.vue';
import InfoPageRacesBlock from '~/components/infoPage/racesBlock.vue';
import InfoPageTextBlock from '~/components/infoPage/textBlock.vue';
import InfoPageTitleBlock from '~/components/infoPage/titleBlock.vue';
import InfoPageVipBlock from '~/components/infoPage/vipBlock.vue';
import { Breadcrumbs } from '~/components/lib/mainPage';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import type { InfoPagePageBlocksDynamicZone } from '~/types/infoPages';

type InfoPageBlockWIthComponent = InfoPagePageBlocksDynamicZone & {
  cmp: any;
  class: string;
};

const props = defineProps({
  pageId: {
    type: String,
    required: true,
  },
  currentInfoPage: {
    type: Object,
    required: true,
  },
});

const storeModals = useStoreModals();
const storeButtonsTitle = useStoreButtonTitles();

const breadcrumbsBlock = computed(() => props.currentInfoPage.breadcrumbs || []);

const isWithCustomFaqStyles = computed(() => props.pageId !== 'faq');
const isNoAdditionalSpaceTop = computed(() => props.pageId === 'app');

const componentsMap = (key: string): Component | null => {
  switch (key) {
    case 'info-page-blocks.vip-block':
      return InfoPageVipBlock;
    case 'info-page-blocks.title-block':
      return InfoPageTitleBlock;
    case 'info-page-blocks.text-block':
      return InfoPageTextBlock;
    case 'info-page-blocks.faq-block':
      return InfoPageFaqBlock;
    case 'info-page-blocks.contact-us-block':
      return InfoPageContactBlock;
    case 'info-page-blocks.payments-block':
      return InfoPagePaymentBlock;
    case 'info-page-blocks.notifications-block':
      return InfoPageNotificationBlock;
    case 'info-page-blocks.promo-bonus-block':
      return InfoPageBonusPromoBlock;
    case 'info-page-blocks.races-block':
      return InfoPageRacesBlock;
    case 'info-page-blocks.alphabet-block':
      return InfoPageAlphabetBlock;
    case 'info-page-blocks.providers-block':
      return InfoPageProvidersBlock;
    case 'info-page-blocks.collections':
      return InfoPageCollections;
    case 'info-page-blocks.categories-block':
      return InfoPageCategoriesBlock;
    case 'info-page-blocks.actions-block':
      return InfoPageActionsBlock;
    // case 'info-page-blocks.actions-banner-block':
    //   return InfoPageActionsBannerBlock;
    case 'info-page-blocks.download-button':
      return InfoPageDownloadFileBlock;
    case 'info-page-blocks.pwa-block':
      return InfoPagePwaBlock;
    case 'info-page-blocks.wheel-block':
      return InfoPageFortuneWheelBlock;
    default:
      return null;
  }
};

const classMap = (typename: string): string => {
  return typename.includes('info-page-blocks.')
    ? typename.slice('info-page-blocks'.length - 1)
    : '';
};

const isPageWithPwaBlock = computed(() =>
  props.currentInfoPage?.pageBlocks.some(
    (block: any) => block.__component === 'info-page-blocks.pwa-block'
  )
);

/**
 *Function for getting data for Info Pages blocks as an array of objects Title, Text, FAQ
 */
const pageBlocks = computed((): InfoPageBlockWIthComponent[] | null => {
  const blocksWithComponents = (props.currentInfoPage?.pageBlocks || []).reduce(
    (newBlocks: any, currBlock: any) => {
      if (currBlock.__component) {
        const className = classMap(currBlock.__component);
        const cmp = componentsMap(currBlock.__component);

        if (cmp) {
          newBlocks.push({
            ...currBlock,
            cmp,
            class: className,
          });
        }
      }
      return newBlocks;
    },
    [] as InfoPageBlockWIthComponent[]
  );

  return blocksWithComponents;
});

const scrollTo = (): void => {
  if (import.meta.browser && window) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

onMounted(() => {
  scrollTo();
});
</script>

<style lang="scss" module src="./infoPage.scss"></style>
